import { Socket, io } from 'socket.io-client'
import {
  ServerToClientEvents,
  ClientToServerEvents,
  Demo2ResponseResult,
  DocumentImageData,
  ILog,
  SessionInfo,
  VideoData,
  AudioData,
  AvatarSessionBootstrap
} from 'types/back'
import { MediaFile, SessionLatencies } from 'types/internal'
import { TwoFactorT } from 'types/twoFactor'
import type * as Proto from 'types/proto'
import { ClientErrorLogsPayload } from 'types/clientErrorLogs'
import config from 'src/config'
import jsLogger from 'js-logger'
import filter from 'lodash/filter'
import map from 'lodash/map'
import round from 'lodash/round'
import { PingStats } from 'shared/utils/pingStats'
import { serializeError } from 'shared/utils/serializeError'

export enum ConnectionState {
  CONNECTING = 0,
  OPEN = 1,
  CLOSING = 2,
  CLOSED = 3
}

const NETWORK_TEST_PERIOD = 3000
const HTTP_ERRORS_FLUSH_PERIOD = 5000
const HTTP_ERRORS_MAX_BUFFER = 200
const LOGS_BATCH_SIZE = 50
const LOGS_RETRY_DELAY = 5000

export class BackClient {
  private _socket: Socket<ServerToClientEvents, ClientToServerEvents> | null =
    null
  private _finished = false
  private _logsBuffer: ILog[] = []
  private _transcriptVerifiedHandler: (v: string) => void = () => null
  private _screenshotProcessedHandler: (faceDetected: boolean) => void = () =>
    null
  private _connectionStatusHandler: (isConnected: boolean) => void = () => null
  private _providedUserDetectedHandler: () => void = () => null
  // Server-side STT (transcriberLocation='server'): server-produced transcripts.
  private _sttInterimHandler: (transcript: string) => void = () => null
  private _sttFinalHandler: (transcript: string) => void = () => null
  // Server-owned turn controller (turnTakingLocation='server', slice 3b): the
  // server pushes the generated phrase and the barge-in interrupt command.
  private _serverPhraseHandler: (response: Demo2ResponseResult) => void = () =>
    null
  private _avatarCancelHandler: () => void = () => null
  // UI-command channel (slice 7, turnTakingLocation='server'): the server tells
  // the client to show the email-capture UI explicitly instead of the client
  // inferring it from an `emailInputHasBeenShown` flag on the phrase.
  private _requestEmailInputHandler: () => void = () => null
  // Explicit end-of-session signal (turnTakingLocation='server'): the server
  // fires this once the final (isOver) phrase has finished playing, so the client
  // navigates to recap directly instead of re-deriving it from serverPhrase.
  private _sessionFinishedHandler: () => void = () => null
  private _videoChunksBuffer: {
    chunk: Blob
    mimeType: string
    role: 'avatar' | 'user'
  }[] = []
  private _isProcessingVideoChunks = false
  private _isProcessingLogs = false
  private _logsRetryTimeout: number = 0
  private _reconnects = 0
  private stats = new PingStats()
  private networkAnalysisInterval: number = 0
  private _pingInFlight = false
  private _sessionInfo: SessionInfo | null = null
  private _errorsLoggerUrl: string | null = null
  private _httpErrorsBuffer: ILog[] = []
  private _errorsFlushInterval: number = 0
  private _isFlushingErrors = false

  // private _isReconnecting = false
  // private _reconnectTimeout: ReturnType<typeof setTimeout> | null = null

  constructor() {
    this.setupLogger()
  }

  public set transcriptVerifiedHandler(f: (v: string) => void) {
    this._transcriptVerifiedHandler = f
  }

  public set screenshotProcessedHandler(f: (faceDetected: boolean) => void) {
    this._screenshotProcessedHandler = f
  }

  public set connectionStatusHandler(f: (isConnected: boolean) => void) {
    this._connectionStatusHandler = f
  }

  public set sttInterimHandler(f: (transcript: string) => void) {
    this._sttInterimHandler = f
  }

  public set sttFinalHandler(f: (transcript: string) => void) {
    this._sttFinalHandler = f
  }

  public set serverPhraseHandler(f: (response: Demo2ResponseResult) => void) {
    this._serverPhraseHandler = f
  }

  public set avatarCancelHandler(f: () => void) {
    this._avatarCancelHandler = f
  }

  public set requestEmailInputHandler(f: () => void) {
    this._requestEmailInputHandler = f
  }

  public set sessionFinishedHandler(f: () => void) {
    this._sessionFinishedHandler = f
  }

  // Slice 6: tell the server the avatar media is playing and the user has
  // interacted (audio playout unlocked), which cues it to voice the greeting.
  public emitAvatarReady = () => {
    if (this._finished || !this._socket) {
      return
    }
    this._socket.emit('demo2AvatarReady')
  }

  // Slice 3b: tell the server to own turn-taking + barge-in for this session, and
  // pass the avatar's interruptionMode so it can replicate the barge-in decision.
  public emitTurnControl = (
    location: 'client' | 'server',
    interruptionMode?: string
  ) => {
    if (this._finished || !this._socket) {
      return
    }
    this._socket.emit('demo2SetTurnControl', { location, interruptionMode })
  }

  // Slice 5 (sessionProvisioning='server'): ask the server to mint the LiveKit
  // room + HeyGen LiveAvatar LITE session and hand back the browser bootstrap
  // ({livekitUrl, roomName, clientToken}). Resolves null (so the caller falls
  // back to the client's own POST to api.liveavatar.com) if the socket is down,
  // the ack times out, or the server isn't configured for provisioning.
  public provisionAvatar = (): Promise<AvatarSessionBootstrap | null> => {
    return new Promise((resolve) => {
      const socket = this._socket
      if (this._finished || !socket) {
        resolve(null)
        return
      }
      let didRespond = false
      const done = (
        session: AvatarSessionBootstrap | null,
        warn?: string,
        extra?: object
      ) => {
        if (didRespond) return
        didRespond = true
        clearTimeout(safetyId)
        if (warn) jsLogger.warn(warn, extra)
        resolve(session)
      }

      // Overall safety net: never block the avatar mount forever. Generous
      // because the socket may still be (re)connecting at session start — the
      // ack itself is fast once we actually emit.
      const safetyId = setTimeout(
        () => done(null, 'provisionAvatar timed out; client falls back'),
        30000
      )

      const emit = () => {
        if (didRespond) return
        socket.emit(
          'demo2ProvisionAvatar',
          { location: 'server' },
          (res: {
            ok: boolean
            error?: string
            session?: AvatarSessionBootstrap
          }) => {
            if (res?.ok && res.session) {
              done(res.session)
            } else {
              done(null, 'provisionAvatar failed; client falls back', {
                error: res?.error
              })
            }
          }
        )
      }

      // Emit only once connected — emitting mid-reconnect races the ack timeout
      // and buffers unpredictably. socket.io fires 'connect' on (re)connect.
      if (socket.connected) {
        emit()
      } else {
        socket.once('connect', emit)
      }
    })
  }

  public set providedUserDetectedHandler(f: () => void) {
    this._providedUserDetectedHandler = f
  }

  private _sendConnectionStatus = (isConnected: boolean) => {
    if (this._connectionStatusHandler) {
      this._connectionStatusHandler(isConnected)
    }
  }

  public start = (sessionInfo: SessionInfo) => {
    this._sessionInfo = sessionInfo
    this.connect(sessionInfo)
    this._processLogs()
    this._processVideoChunks()
    this._flushErrorsHttp()
  }

  private connect = (sessionInfo: SessionInfo) => {
    jsLogger.log('Back: connect', sessionInfo)
    if (this._socket) {
      this._socket.disconnect()
      this._socket = null
    }

    this._socket = io(config.socketServerUrl, {
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 3000,
      reconnectionAttempts: Infinity,
      transports: ['websocket'],
      timeout: 2000,
      forceNew: true,
      auth: sessionInfo
    })

    this._bindSocketEvents()
  }

  private setupLogger = () => {
    const consoleHandler = jsLogger.createDefaultHandler({
      formatter: function (messages, context) {
        // prefix each log message with a timestamp.
        messages.unshift(`${context.level.name}:`)
      }
    })

    jsLogger.setHandler((messages, context) => {
      consoleHandler(messages, context)
      this.sendLog(messages, context)
      if (context.level.name === 'ERROR') {
        this._bufferHttpError(messages, context)
      }
    })
  }

  public initErrorsLogger = (url: string) => {
    if (this._errorsLoggerUrl) {
      jsLogger.warn('initErrorsLogger: already initialized', {
        existing: this._errorsLoggerUrl
      })
      return
    }
    this._errorsLoggerUrl = url
    this._errorsFlushInterval = window.setInterval(
      this._flushErrorsHttp,
      HTTP_ERRORS_FLUSH_PERIOD
    )
    window.addEventListener('pagehide', this._flushErrorsHttp)
    jsLogger.log('initErrorsLogger: ready', { url })
  }

  private _bufferHttpError = (messages: any[], context: any) => {
    if (!this._errorsLoggerUrl) return
    const serializedMessages = map(messages, serializeError)
    this._httpErrorsBuffer.push({
      timestamp: Date.now(),
      messages: serializedMessages,
      context: {
        level: context?.level?.name,
        callsite: this._captureCallsite()
      }
    })
    if (this._httpErrorsBuffer.length > HTTP_ERRORS_MAX_BUFFER) {
      this._httpErrorsBuffer.splice(
        0,
        this._httpErrorsBuffer.length - HTTP_ERRORS_MAX_BUFFER
      )
    }
  }

  private _captureCallsite = (): string | undefined => {
    const stack = new Error().stack
    if (!stack) return undefined
    const userFrames = filter(
      stack.split('\n'),
      (line: string) =>
        !!line &&
        !line.trim().startsWith('Error') &&
        !line.includes('_bufferHttpError') &&
        !line.includes('_captureCallsite') &&
        !line.includes('setHandler') &&
        !line.includes('js-logger')
    )
    const top = userFrames.slice(0, 3).join('\n').trim()
    return top || undefined
  }

  private _flushErrorsHttp = async () => {
    if (this._isFlushingErrors) return
    if (!this._errorsLoggerUrl) return
    if (!this._sessionInfo) return
    if (this._httpErrorsBuffer.length === 0) return

    this._isFlushingErrors = true
    const logsToSend = this._httpErrorsBuffer.slice()
    const payload: ClientErrorLogsPayload = {
      sessionId: this._sessionInfo.sessionId,
      logs: logsToSend
    }

    try {
      const res = await fetch(this._errorsLoggerUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      })
      if (res.ok) {
        this._httpErrorsBuffer.splice(0, logsToSend.length)
      }
    } catch {
      // network error — keep buffer, retry on next tick
    } finally {
      this._isFlushingErrors = false
    }
  }

  private _bindSocketEvents(): void {
    if (this._socket) {
      this._socket.on('connect', () => {
        jsLogger.log('BACK: socket connected')
        this._sendConnectionStatus(true)
        this._processLogs()
        this._processVideoChunks()
        this._startNetworkAnalysis()
        // Slice 2: resync the server's soft avatar-status mirror after a
        // (re)connect — it resets when the socket drops. Only fires once the
        // session has reported at least one real transition (i.e. opted in).
        if (this._lastAvatarState) {
          this._socket?.emit('avatarState', {
            status: this._lastAvatarState.status,
            reason: 'snapshot',
            qId: this._lastAvatarState.qId
          })
        }
      })

      this._socket.on('transcriptVerified', (transcript: string) => {
        jsLogger.log('BACK: transcriptVerified received', { transcript })
        this._sendConnectionStatus(true)
        this._transcriptVerifiedHandler(transcript)
      })

      this._socket.on('screenshotProcessed', (faceDetected: boolean) => {
        jsLogger.log('BACK: screenshotProcessed received', { faceDetected })
        this._sendConnectionStatus(true)
        this._screenshotProcessedHandler(faceDetected)
      })

      this._socket.on('providedUserDetected', () => {
        jsLogger.log('BACK: providedUserDetected received')
        this._sendConnectionStatus(true)
        this._providedUserDetectedHandler()
      })

      this._socket.on('sttInterim', ({ transcript }) => {
        this._sttInterimHandler(transcript)
      })

      this._socket.on('sttFinal', ({ transcript }) => {
        this._sttFinalHandler(transcript)
      })

      // Slice 3b (turnTakingLocation='server'): the server pushes the generated
      // phrase and the barge-in interrupt instead of the client driving them.
      this._socket.on('serverPhrase', (response) => {
        this._serverPhraseHandler(response as Demo2ResponseResult)
      })

      this._socket.on('avatarCancel', () => {
        this._avatarCancelHandler()
      })

      // Slice 7 (turnTakingLocation='server'): explicit server→client UI command
      // to show the email-capture UI, replacing the client's flag inference.
      this._socket.on('requestEmailInput', () => {
        jsLogger.log('BACK: requestEmailInput received')
        this._requestEmailInputHandler()
      })

      // Explicit end-of-session signal: the server fires this once the final
      // (isOver) phrase has finished playing → navigate to recap directly.
      this._socket.on('sessionFinished', () => {
        jsLogger.log('BACK: sessionFinished received')
        this._sessionFinishedHandler()
      })

      this._socket.io.on('reconnect_attempt', (attempting) => {
        jsLogger.warn('BACK: socket event: reconnect_attempt', { attempting })
        this._reconnects++
      })

      this._socket.io.on('reconnect_error', (e) => {
        jsLogger.warn('BACK: socket event: reconnect_error', { e })
      })

      this._socket.io.on('reconnect_failed', () => {
        jsLogger.warn('BACK: socket event: reconnect_failed')
      })

      this._socket.on('disconnect', () => {
        if (this._finished) {
          jsLogger.warn('BACK DISCONNECTED, the session is finished')
          return
        } else {
          jsLogger.warn('BACK DISCONNECTED, attempting to reconnect...')
          // this._scheduleReconnect()
        }
        this._sendConnectionStatus(false)
      })

      this._socket.on('connect_error', (error) => {
        jsLogger.error('Connection error:', error, {
          url: config.socketServerUrl,
          online: navigator.onLine,
          attempt: this._reconnects,
          transport: this._socket?.io?.engine?.transport?.name,
          description: (error as any)?.description
        })
        this._sendConnectionStatus(false)
      })
    }
  }

  private ping = async (timeoutMs = 2000): Promise<number | null> => {
    if (!this._socket?.connected || this._pingInFlight) {
      return null
    }

    this._pingInFlight = true

    return new Promise<number | null>((resolve) => {
      const start = performance.now()

      const timer = window.setTimeout(() => {
        this._pingInFlight = false
        resolve(null)
      }, timeoutMs)

      this._socket?.emit('rtt', () => {
        clearTimeout(timer)
        this._pingInFlight = false
        resolve(round(performance.now() - start, 2))
      })
    })
  }

  private _startNetworkAnalysis = () => {
    this.networkAnalysisInterval = window.setInterval(async () => {
      if (!this._socket?.connected) return

      const rtt = await this.ping()

      if (typeof rtt === 'number') {
        this.stats.add(rtt)

        const quality = this.getConnectionQuality()

        jsLogger.log('Back: Network', {
          rtt: Math.round(rtt),
          pings: this.stats.values,
          avgPing: this.stats.avg,
          reconnects: this._reconnects,
          jitter: Math.round(this.stats.jitter),
          quality
        })
      }
    }, NETWORK_TEST_PERIOD)
  }

  public getConnectionQuality = () => {
    const ping = this.stats.avg
    const jitter = this.stats.jitter
    if (this._reconnects > 2) return 'bad'
    if (ping < 100 && jitter < 20) return 'excellent'
    if (ping < 200 && jitter < 50) return 'good'
    if (ping < 400) return 'poor'
    return 'bad'
  }

  public sendLog = (messages: any[], context: object) => {
    if (!this._finished) {
      // console.log('BACK: send log')
      const l: ILog = {
        messages,
        context,
        timestamp: Date.now()
      }

      this._logsBuffer.push(l)
      this._processLogs()
    }
  }

  private _processLogs = () => {
    if (this._isProcessingLogs) return
    this._isProcessingLogs = true

    // jsLogger.log(
    //   'BACK: _processLogs, _logsBuffer.length',
    //   this._logsBuffer.length
    // )

    // Use a timeout for the socket emit acknowledgement (e.g., 3000ms)
    const sendAllLogs = async () => {
      if (
        this._socket &&
        this._socket.connected &&
        this._logsBuffer.length > 0
      ) {
        const logsToSend = this._logsBuffer.slice(0, LOGS_BATCH_SIZE)
        const TIMEOUT_MS = 3000

        try {
          await new Promise<void>((resolve, reject) => {
            let didRespond = false
            const timeoutId = setTimeout(() => {
              if (!didRespond) {
                didRespond = true
                reject(new Error('socket log ack timed out'))
              }
            }, TIMEOUT_MS)
            this._socket?.emit(
              'log',
              logsToSend,
              (res: { ok: boolean; error?: string }) => {
                if (didRespond) return
                didRespond = true
                clearTimeout(timeoutId)
                if (res?.ok) {
                  this._sendConnectionStatus(true)
                  resolve()
                } else {
                  reject(new Error(res?.error || 'ack failed'))
                }
              }
            )
          })

          // ✅ success, clear buffer
          this._logsBuffer.splice(0, logsToSend.length)
        } catch (err) {
          jsLogger.warn('Failed to send logs:', err)
          this._sendConnectionStatus(false)
          this._isProcessingLogs = false
          if (!this._finished) {
            this._logsRetryTimeout = window.setTimeout(
              () => this._processLogs(),
              LOGS_RETRY_DELAY
            )
          }
          return
        }
      }

      this._isProcessingLogs = false
      // If any new logs came in during sending, process them no w
      if (
        this._socket &&
        this._socket.connected &&
        this._logsBuffer.length > 0
      ) {
        // Run again to flush new logs
        this._processLogs()
      } else if (this._finished) {
        this.closeConnection()
      }
    }

    sendAllLogs()
  }

  private _processVideoChunks = async () => {
    if (this._isProcessingVideoChunks) return
    this._isProcessingVideoChunks = true

    // jsLogger.log(
    //   'BACK: _processVideoChunks, _videoChunksBuffer.length',
    //   this._videoChunksBuffer.length
    // )

    // TODO: pass rotation
    const rotation = 0

    const processNextChunk = async () => {
      if (
        this._socket &&
        this._socket.connected &&
        this._videoChunksBuffer.length > 0
      ) {
        const { chunk, mimeType, role } = this._videoChunksBuffer[0] // 👈 peek (don’t remove yet)

        // jsLogger.log('Back, processing chunks buffer, the first chunk is', {
        //   chunkSize: chunk.size,
        //   mimeType,
        //   role
        // })

        try {
          await new Promise<void>((resolve, reject) => {
            let didRespond = false
            const timeoutId = setTimeout(() => {
              if (!didRespond) {
                didRespond = true
                reject(new Error('videoChunk ack timed out'))
              }
            }, 5000) // You can adjust the timeout duration if needed

            const videoData: VideoData = {
              chunk,
              mimeType,
              role,
              rotation
            }
            this._socket?.emit(
              'videoChunk',
              videoData,
              (res: { ok: boolean; error?: string }) => {
                if (didRespond) return
                didRespond = true
                clearTimeout(timeoutId)
                if (res?.ok) {
                  this._sendConnectionStatus(true)
                  resolve()
                } else {
                  reject(new Error(res?.error || 'ack failed'))
                }
              }
            )
          })
          // ✅ success, remove from buffer and send next
          this._videoChunksBuffer.shift()
          jsLogger.log('Back, the chunk is sent', {
            chunkSize: chunk.size,
            mimeType,
            role,
            chunksBuffer: this._videoChunksBuffer.length
          })
          if (this._finished) {
            this.closeConnection()
          }
          setTimeout(processNextChunk, 0)
        } catch (err) {
          jsLogger.warn('Failed to send chunk:', {
            chunkSize: chunk.size,
            mimeType,
            role,
            err
          })
          // keep the chunk in the buffer, try again later
          this._sendConnectionStatus(false)
          this._isProcessingVideoChunks = false
          return
        }
      } else {
        this._isProcessingVideoChunks = false
        if (this._finished) {
          this.closeConnection()
        }
      }
    }

    processNextChunk()
  }

  public sendVideoChunk = (
    chunk: Blob,
    mimeType: string,
    role: 'avatar' | 'user'
  ) => {
    // jsLogger.log('Back: sendVideoChunk', {
    //   chunkSize: chunk.size,
    //   mimeType,
    //   role
    // })
    if (!this._finished) {
      this._videoChunksBuffer.push({ chunk, mimeType, role })
      this._processVideoChunks()
    }

    return this._videoChunksBuffer.length
  }

  // Server-side STT (slice 1): stream a raw 16-bit PCM frame to the server in
  // addition to the video. Fire-and-forget (NOT ack-gated like videoChunk) so
  // audio is never queued behind the ack-paced video chunks. `pcm` is the exact
  // buffer already fed to the client's own transcriber.
  public emitAudioChunk = (pcm: ArrayBuffer, sampleRate: number) => {
    if (this._finished || !this._socket) {
      return
    }
    const audioData: AudioData = {
      chunk: pcm,
      mimeType: 'audio/pcm',
      sampleRate,
      channels: 1,
      encoding: 'pcm16',
      role: 'user'
    }
    this._socket.emit('audioChunk', audioData)
  }

  // Server-side control migration (slice 2): forward the avatar's LiveKit
  // lifecycle up the socket so the server can mirror avatar status (see
  // twoFactorProcessor.setAvatarState). Fire-and-forget; the caller gates on the
  // `reportAvatarState` config flag, so the server mirror is inert unless a
  // session opts in. The last real transition is cached and re-emitted as a
  // 'snapshot' on reconnect, since the server mirror is soft state that resets
  // when the socket drops.
  private _lastAvatarState: {
    status: 'initializing' | 'talking' | 'idle'
    qId?: string
  } | null = null

  public emitAvatarState = (
    status: 'initializing' | 'talking' | 'idle',
    reason:
      | 'initialized'
      | 'speak_started'
      | 'speak_ended'
      | 'interrupted'
      | 'snapshot',
    qId?: string
  ) => {
    if (this._finished || !this._socket) {
      return
    }
    if (reason !== 'snapshot') {
      this._lastAvatarState = { status, qId }
    }
    this._socket.emit('avatarState', { status, reason, qId })
  }

  public sendDocumentImage = (
    image: DocumentImageData
  ): Promise<MediaFile> => {
    return new Promise((resolve, reject) => {
      if (!this._socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }

      let didRespond = false
      const timeoutId = setTimeout(() => {
        if (!didRespond) {
          didRespond = true
          reject(new Error('documentImage ack timed out'))
        }
      }, 10000)

      this._socket.emit(
        'documentImage',
        image,
        (res: { ok: boolean; error?: string; file?: MediaFile }) => {
          if (didRespond) return
          didRespond = true
          clearTimeout(timeoutId)
          if (res?.ok && res.file) {
            jsLogger.log('Document image sent', {
              side: image.side,
              type: image.type,
              size: image.blob.size,
              file: res.file
            })
            resolve(res.file)
          } else {
            reject(new Error(res?.error || 'documentImage ack failed'))
          }
        }
      )
    })
  }

  public sendInterimTranscript = (transcript: string) => {
    this._socket?.emit('interimTranscript', transcript)
  }

  // Submit the user's manually-entered email over the socket instead of the FF
  // /demo2/:id/submitEmail endpoint. Creates the verification + sends the code.
  public demo2SubmitEmail = (email: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!this._socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }
      let didRespond = false
      const timeoutId = setTimeout(() => {
        if (!didRespond) {
          didRespond = true
          reject(new Error('demo2SubmitEmail ack timed out'))
        }
      }, 15000)
      this._socket.emit(
        'demo2SubmitEmail',
        { email },
        (res: { ok: boolean; error?: string }) => {
          if (didRespond) return
          didRespond = true
          clearTimeout(timeoutId)
          if (res?.ok) resolve()
          else reject(new Error(res?.error || 'demo2SubmitEmail ack failed'))
        }
      )
    })
  }

  // Skip the email input over the socket instead of FF /demo2/:id/skipEmailInput.
  public demo2SkipEmail = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!this._socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }
      let didRespond = false
      const timeoutId = setTimeout(() => {
        if (!didRespond) {
          didRespond = true
          reject(new Error('demo2SkipEmail ack timed out'))
        }
      }, 15000)
      this._socket.emit(
        'demo2SkipEmail',
        (res: { ok: boolean; error?: string }) => {
          if (didRespond) return
          didRespond = true
          clearTimeout(timeoutId)
          if (res?.ok) resolve()
          else reject(new Error(res?.error || 'demo2SkipEmail ack failed'))
        }
      )
    })
  }

  // Commit the avatar's phrase over the socket instead of the FF
  // /two_factor/applyResponse endpoint. Called when the avatar starts speaking.
  public demo2ApplyResponse = (
    q: TwoFactorT.QuestionT
  ): Promise<{ steps?: TwoFactorT.StepT[] }> => {
    return new Promise((resolve, reject) => {
      if (!this._socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }
      let didRespond = false
      const timeoutId = setTimeout(() => {
        if (!didRespond) {
          didRespond = true
          reject(new Error('demo2ApplyResponse ack timed out'))
        }
      }, 15000)
      this._socket.emit(
        'demo2ApplyResponse',
        { q },
        (res: {
          ok: boolean
          error?: string
          steps?: TwoFactorT.StepT[]
        }) => {
          if (didRespond) return
          didRespond = true
          clearTimeout(timeoutId)
          if (res?.ok) resolve({ steps: res.steps })
          else reject(new Error(res?.error || 'demo2ApplyResponse ack failed'))
        }
      )
    })
  }

  // Realtime barge-in check over the socket instead of the FF
  // /two_factor/checkInterrupt endpoint. Resolves to whether to interrupt.
  public demo2CheckInterrupt = (reply: string): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      if (!this._socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }
      let didRespond = false
      const timeoutId = setTimeout(() => {
        if (!didRespond) {
          didRespond = true
          reject(new Error('demo2CheckInterrupt ack timed out'))
        }
      }, 15000)
      this._socket.emit(
        'demo2CheckInterrupt',
        { reply },
        (res: { ok: boolean; error?: string; interrupt?: boolean }) => {
          if (didRespond) return
          didRespond = true
          clearTimeout(timeoutId)
          if (res?.ok) resolve(!!res.interrupt)
          else reject(new Error(res?.error || 'demo2CheckInterrupt ack failed'))
        }
      )
    })
  }

  // Set the interaction language over the socket instead of FF
  // /two_factor/:id/setLang. Fire-and-forget from the caller (low priority).
  public demo2SetLang = (lang: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!this._socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }
      let didRespond = false
      const timeoutId = setTimeout(() => {
        if (!didRespond) {
          didRespond = true
          reject(new Error('demo2SetLang ack timed out'))
        }
      }, 15000)
      this._socket.emit(
        'demo2SetLang',
        { lang },
        (res: { ok: boolean; error?: string }) => {
          if (didRespond) return
          didRespond = true
          clearTimeout(timeoutId)
          if (res?.ok) resolve()
          else reject(new Error(res?.error || 'demo2SetLang ack failed'))
        }
      )
    })
  }

  // Persist session-level latencies over the socket instead of FF
  // /demo2/:id/latencies. Fire-and-forget from the caller (low priority).
  public demo2SendLatencies = (
    latencies: Partial<SessionLatencies>
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!this._socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }
      let didRespond = false
      const timeoutId = setTimeout(() => {
        if (!didRespond) {
          didRespond = true
          reject(new Error('demo2SendLatencies ack timed out'))
        }
      }, 15000)
      this._socket.emit(
        'demo2SendLatencies',
        { latencies },
        (res: { ok: boolean; error?: string }) => {
          if (didRespond) return
          didRespond = true
          clearTimeout(timeoutId)
          if (res?.ok) resolve()
          else reject(new Error(res?.error || 'demo2SendLatencies ack failed'))
        }
      )
    })
  }

  // Persist avatar/heyGen latencies onto a phrase over the socket instead of FF
  // /demo2/:id/avatar_latency. Fire-and-forget from the caller (low priority).
  public demo2SendAvatarLatency = (
    params: Proto.AvatarLatencyRequest
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!this._socket?.connected) {
        reject(new Error('Socket not connected'))
        return
      }
      let didRespond = false
      const timeoutId = setTimeout(() => {
        if (!didRespond) {
          didRespond = true
          reject(new Error('demo2SendAvatarLatency ack timed out'))
        }
      }, 15000)
      this._socket.emit(
        'demo2SendAvatarLatency',
        params,
        (res: { ok: boolean; error?: string }) => {
          if (didRespond) return
          didRespond = true
          clearTimeout(timeoutId)
          if (res?.ok) resolve()
          else reject(new Error(res?.error || 'demo2SendAvatarLatency ack failed'))
        }
      )
    })
  }

  private resetHandlers = () => {
    this._transcriptVerifiedHandler = () => null
    this._screenshotProcessedHandler = () => null
    this._connectionStatusHandler = () => null
    this._providedUserDetectedHandler = () => null
    this._sttInterimHandler = () => null
    this._sttFinalHandler = () => null
    this._serverPhraseHandler = () => null
    this._avatarCancelHandler = () => null
    this._requestEmailInputHandler = () => null
  }

  private closeConnection = () => {
    if (this._logsBuffer.length === 0 && this._videoChunksBuffer.length === 0) {
      jsLogger.log('Back: buffers are empty, closing the connection')
      this._socket?.emit('end')
      setTimeout(() => {
        if (this._socket && this._socket.connected) {
          this._socket?.disconnect()
          this._socket?.close()
        }
      }, 500)
    } else {
      jsLogger.log(`Back: closeConnection, buffers are not empty`, {
        logsBufferSize: this._logsBuffer.length,
        videoBufferSize: this._videoChunksBuffer.length
      })
    }
  }

  public disconnect(): void {
    if (this._finished) {
      jsLogger.log(`Back: disconnect: already finished`)
    } else {
      this._finished = true
      this.resetHandlers()
      clearInterval(this.networkAnalysisInterval)
      if (this._logsRetryTimeout) {
        clearTimeout(this._logsRetryTimeout)
        this._logsRetryTimeout = 0
      }
      if (this._errorsFlushInterval) {
        clearInterval(this._errorsFlushInterval)
        this._errorsFlushInterval = 0
      }
      window.removeEventListener('pagehide', this._flushErrorsHttp)
      this._flushErrorsHttp()
      if (this._socket) {
        jsLogger.log(`Back: disconnect, buffers`, {
          logsBufferSize: this._logsBuffer.length,
          videoBufferSize: this._videoChunksBuffer.length
        })
        this.closeConnection()
      }
    }
  }
}

let cl: BackClient | null = null

export const getBackClient = () => {
  if (cl) {
    return cl
  } else {
    jsLogger.log('BACK: new BackClient')
    cl = new BackClient()
    return cl
  }
}
